import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Divider, Skeleton } from '@mui/material';

import SearchBar from '@components/searchbar/SearchBar';
import Tooltip from '@components/tooltip/Tooltip';
import useOnClickOutside from '@utils/hooks';
import { DropdownAction, DropdownOption, DropdownProps } from 'types/dropdown';

import Button from '../button/Button';
import Icon from '../icon/Icon';

import theme from '../../../themes/colors';

const PORTAL_GAP = 4;

export const DROPDOWN_VARIANTS = {
  default: {
    buttonClassName: '',
    optionsStyle: 'right-0',
    portalPanelClassName: '',
    placeholderStyle: '',
    optionItemClassName: '',
    selectedOptionClassName: 'bg-blue4',
    hoverOptionClassName: 'hover:bg-gray-100',
    isDividerNeeded: true,
    clearInField: false
  },
  v2: {
    buttonClassName:
      'h-11 min-w-45 rounded-lg border !border-cofeeNeutral5 !bg-cofeeNeutral1',
    optionsStyle:
      'py-2 right-0 !min-w-45 !border-cofeeNeutral4 !shadow-boxShadowSingle1 !shadow-boxShadowSingle2 !rounded-xl',
    portalPanelClassName:
      'py-2 !border-cofeeNeutral4 !shadow-boxShadowSingle1 !shadow-boxShadowSingle2 !rounded-xl',
    placeholderStyle: '!text-cofeeNeutral10',
    optionItemClassName:
      '!text-cofeeNeutral10 !font-medium !text-sm my-0 !pl-2',
    selectedOptionClassName: '!bg-transparent',
    hoverOptionClassName: 'hover:bg-gray-100',
    isDividerNeeded: false,
    clearInField: false
  },
  v3: {
    buttonClassName:
      'h-11 w-full min-w-0 rounded-lg border !border-cofeeNeutral5 !bg-cofeeNeutral1',
    optionsStyle:
      'py-2 left-0 right-0 !w-full !min-w-full !border-cofeeNeutral4 !shadow-boxShadowSingle1 !shadow-boxShadowSingle2 !rounded-xl',
    portalPanelClassName:
      'py-2 !border-cofeeNeutral4 !shadow-boxShadowSingle1 !shadow-boxShadowSingle2 !rounded-xl',
    placeholderStyle: '!text-cofeeNeutral10',
    optionItemClassName:
      '!text-cofeeNeutral10 !font-medium !text-sm my-0 !pl-2',
    selectedOptionClassName: '!bg-transparent',
    hoverOptionClassName: 'hover:bg-gray-100',
    isDividerNeeded: false,
    clearInField: true
  }
} as const;

const PORTAL_CONFLICTING_CLASSES = new Set([
  'left-0',
  '!left-0',
  'right-0',
  '!right-0',
  'w-full',
  '!w-full'
]);

const stripPositionClasses = (className: string) =>
  className
    .split(/\s+/)
    .filter((cls) => cls && !PORTAL_CONFLICTING_CLASSES.has(cls))
    .join(' ');

export const resolvePortalPanelClassName = (
  variant: keyof typeof DROPDOWN_VARIANTS,
  optionsStyle?: string
) => {
  const variantStyles = DROPDOWN_VARIANTS[variant];
  const explicitExtra = optionsStyle ? stripPositionClasses(optionsStyle) : '';

  return [variantStyles.portalPanelClassName, explicitExtra]
    .filter(Boolean)
    .join(' ');
};

const computePortalDropdownPosition = (
  rect: DOMRectReadOnly,
  optionCount: number,
  hasSearchBar: boolean,
  hasDropdownAction: boolean
) => {
  const estimatedHeight = Math.min(
    220,
    16 +
      (hasSearchBar ? 56 : 0) +
      (hasDropdownAction ? 48 : 0) +
      Math.max(optionCount, 1) * 48
  );

  const gap = PORTAL_GAP;
  const viewportHeight = window.innerHeight;

  const spaceBelow = viewportHeight - rect.bottom - gap;
  const spaceAbove = rect.top - gap;

  const shouldOpenUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

  const base = {
    left: rect.left,
    width: rect.width
  };

  return shouldOpenUp
    ? {
        ...base,
        top: 0,
        bottom: viewportHeight - rect.top + gap
      }
    : {
        ...base,
        top: rect.bottom + gap,
        bottom: null
      };
};

const Dropdown = (props: DropdownProps) => {
  const {
    filterLabel,
    dropdownId,
    currentFilter,
    filterOptions,
    onSelectFilter,
    className,
    optionsStyle,
    labelStyle,
    isLoading,
    placeholder,
    loadingStyles,
    skeletonClassName,
    optionsCount,
    placeholderStyle,
    dropdownHeaderStyle,
    listId,
    disabled,
    placeholderNode,
    searchBarPlaceholder,
    dropdownAction,
    buttonClassName,
    stopEventPropagation = false,
    isSearchDigitsOnly = false,
    tooltipText = '',
    usePortal = false,
    closeOnOutsideScroll = false,
    variant = 'default',
    isDividerNeeded,
    selectedOptionClassName,
    hoverOptionClassName,
    optionItemClassName,
    onClear,
    dropdownActionClassName,
    clearInField,
    rightIconStyle
  } = props;

  const variantStyles = DROPDOWN_VARIANTS[variant];
  const resolvedButtonClassName =
    buttonClassName ?? variantStyles.buttonClassName;
  const resolvedOptionsStyle = optionsStyle ?? variantStyles.optionsStyle;
  const resolvedPlaceholderStyle =
    placeholderStyle ?? variantStyles.placeholderStyle;
  const resolvedOptionItemClassName =
    optionItemClassName ?? variantStyles.optionItemClassName;
  const resolvedSelectedOptionClassName =
    selectedOptionClassName ?? variantStyles.selectedOptionClassName;
  const resolvedHoverOptionClassName =
    hoverOptionClassName ?? variantStyles.hoverOptionClassName;
  const resolvedIsDividerNeeded =
    isDividerNeeded ?? variantStyles.isDividerNeeded;
  const resolvedClearInField = clearInField ?? variantStyles.clearInField;
  const resolvedPortalPanelClassName = resolvePortalPanelClassName(
    variant,
    optionsStyle
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    bottom: number | null;
  }>({ top: 0, left: 0, width: 0, bottom: null });
  const [isPositionReady, setIsPositionReady] = useState<boolean>(false);

  const ref = useRef(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = (evt?: React.MouseEvent) => {
    setIsDropdownOpen((isOpen) => !isOpen);
    setSearchTerm('');
    if (usePortal) {
      setIsPositionReady(false);
    }
    if (stopEventPropagation) {
      evt?.stopPropagation();
    }
  };

  const onClickFilter = (option: DropdownOption, evt?: React.MouseEvent) => {
    if (stopEventPropagation || usePortal) {
      evt?.stopPropagation();
    }
    if (option.value !== currentFilter && !option.isDisabled) {
      onSelectFilter(option.value);
      toggleDropdown();
    }
  };

  const selectedFilter: DropdownOption | undefined = filterOptions?.find(
    (option) => option.value === currentFilter
  );

  const filteredOptions: DropdownOption[] = searchTerm
    ? filterOptions.filter((option: DropdownOption) =>
        (option.label?.toLowerCase() || '').includes(
          searchTerm?.toLowerCase() || ''
        )
      )
    : filterOptions;

  useOnClickOutside(usePortal ? dropdownRef : ref, (e) => {
    if (
      usePortal &&
      ref.current &&
      typeof (ref.current as HTMLElement).contains === 'function' &&
      (ref.current as HTMLElement).contains(e.target as Node)
    ) {
      return;
    }
    e.stopPropagation();
    setIsDropdownOpen(false);
  });

  useEffect(() => {
    if (usePortal && isDropdownOpen && ref.current) {
      const updatePosition = () => {
        if (!ref.current) return;
        const rect = (ref.current as HTMLElement).getBoundingClientRect();
        setPosition(
          computePortalDropdownPosition(
            rect,
            filteredOptions.length,
            Boolean(searchBarPlaceholder),
            Boolean(dropdownAction)
          )
        );
        setIsPositionReady(true);
      };

      updatePosition();

      const handleScroll = (event: Event) => {
        if (!closeOnOutsideScroll) {
          updatePosition();
          return;
        }
        if (
          event.target instanceof Node &&
          dropdownRef.current?.contains(event.target)
        ) {
          return;
        }
        setIsDropdownOpen(false);
      };
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', updatePosition);
      };
    }

    if (!usePortal && isDropdownOpen) {
      const element = document.getElementById(`${listId}-dropdown-list`);
      if (element) {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (rect.bottom > viewportHeight) {
          element.style.bottom = '100%';
          element.style.top = 'auto';
        } else {
          element.style.top = '100%';
          element.style.bottom = 'auto';
        }
      }
    }

    return undefined;
  }, [
    isDropdownOpen,
    listId,
    usePortal,
    closeOnOutsideScroll,
    filteredOptions.length,
    searchBarPlaceholder,
    dropdownAction,
    searchTerm
  ]);

  if (isLoading)
    return (
      <div className={loadingStyles || 'w-full'}>
        <Skeleton
          height={53}
          key={Math.random()}
          animation="wave"
          variant="rounded"
          className={skeletonClassName}
        />
      </div>
    );

  const onClickDropdownAction = (evt?: React.MouseEvent) => {
    if (stopEventPropagation || usePortal) {
      evt?.stopPropagation();
    }
    setIsDropdownOpen(false);
    dropdownAction?.onClick();
  };

  const renderDropdownAction = (action: DropdownAction) => (
    <div className="-mx-2 border-b border-cofeeGreyBlue300">
      <div
        className={`flex cursor-pointer items-center gap-2 pb-4 pl-3 pr-4 pt-2 text-base font-normal text-cofeeNeutral10 hover:bg-transparent ${dropdownActionClassName}`}
        role="presentation"
        onClick={onClickDropdownAction}
      >
        <Icon
          name={action.icon ?? 'plus'}
          size="medium"
          stroke={theme.cofeePrimary5}
        />
        {action.label}
      </div>
    </div>
  );

  const renderSearchBar = (searchPlaceholder: string) => (
    <div className="my-3">
      <SearchBar
        onSearch={(searchValue) => {
          if (searchTerm === searchValue) return;
          setSearchTerm(searchValue);
        }}
        placeholder={searchPlaceholder}
        value={searchTerm}
        stopEventPropagation={stopEventPropagation}
        isSearchDigitsOnly={isSearchDigitsOnly}
      />
    </div>
  );

  const renderDropdown = () => (
    <>
      {dropdownAction && renderDropdownAction(dropdownAction)}
      {searchBarPlaceholder && renderSearchBar(searchBarPlaceholder)}
      {filteredOptions.length > 0 ? (
        filteredOptions?.map((option, index) => (
          <div key={option?.id}>
            <div
              id={`${filterLabel || 'dropdown'}_${option.value}`}
              className={`my-1 flex items-center justify-between rounded-lg py-2 pl-3 pr-4 text-sm first:pt-3 last:pb-3 ${resolvedOptionItemClassName} ${
                option.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              } ${
                option.value === selectedFilter?.value
                  ? `${resolvedSelectedOptionClassName}`
                  : `${resolvedHoverOptionClassName}`
              }`}
              role="presentation"
              onClick={(evt) => onClickFilter(option, evt)}
            >
              {option.element ?? option.label}
              {option.value === selectedFilter?.value &&
                (onClear && !resolvedClearInField ? (
                  <div
                    role="presentation"
                    className="ml-2 cursor-pointer rounded p-1 hover:bg-red-50"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      onClear();
                      toggleDropdown();
                    }}
                  >
                    <Icon
                      name="trash-red-2"
                      className="!h-5 !min-h-5 !w-5 !min-w-5"
                    />
                  </div>
                ) : (
                  <Icon
                    name="check-mark"
                    className="ml-2 !h-4 !min-h-4 !w-4 !min-w-4"
                    stroke={theme.primary}
                  />
                ))}
            </div>
            {resolvedIsDividerNeeded && index + 1 !== filterOptions.length && (
              <Divider className="border-grey40" />
            )}
          </div>
        ))
      ) : (
        <div className="mt-3 flex h-40 items-center justify-center px-6 text-base text-grey">
          No results found
        </div>
      )}
    </>
  );

  const showFieldClear =
    resolvedClearInField && !!selectedFilter && !!onClear && !disabled;

  const triggerButton = (
    <Button
      data-testid={dropdownId}
      variant="tertiary"
      handleClick={toggleDropdown}
      size="medium"
      label={
        (selectedFilter?.element ?? selectedFilter?.label) ||
        (placeholderNode ?? placeholder)
      }
      labelCount={optionsCount}
      rightIconName="down-icon-dark"
      className={`min-w-166 justify-between border ${
        className || resolvedButtonClassName
      }`}
      labelStyle={
        placeholder && !selectedFilter?.label
          ? `!text-base text-grey !font-normal ${
              resolvedPlaceholderStyle || ''
            }`
          : `!font-normal ${labelStyle || ''}`
      }
      rightIconStyle={`${
        isDropdownOpen ? '-rotate-180' : ''
      } ${rightIconStyle} `}
      disabled={disabled}
    />
  );

  return (
    <div className={`relative ${className || ''}`} ref={ref}>
      <div
        className={`flex items-center gap-2 ${dropdownHeaderStyle || ''}`}
        data-tooltip-id={`${dropdownId}-label-tooltip`}
      >
        {filterLabel && (
          <span className="text-base font-medium text-grey">{filterLabel}</span>
        )}
        {resolvedClearInField ? (
          <div className="relative flex w-full">
            {triggerButton}
            {showFieldClear && (
              <div
                role="presentation"
                className="absolute right-12 top-1/2 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-cofeeGreyBlue75"
                onClick={(evt) => {
                  evt.stopPropagation();
                  onClear?.();
                }}
              >
                <Icon name="close" size="small" />
              </div>
            )}
          </div>
        ) : (
          triggerButton
        )}
      </div>
      {tooltipText && (
        <Tooltip
          id={`${dropdownId}-label-tooltip`}
          type="text"
          tooltipText={tooltipText}
        />
      )}
      {isDropdownOpen && usePortal && isPositionReady
        ? createPortal(
            <div
              ref={dropdownRef}
              className={`custom-scroll fixed z-[9999] mt-1 max-h-55 w-55 overflow-y-auto rounded-md border border-grey40 bg-theme px-2 shadow-sm ${resolvedPortalPanelClassName}`}
              style={{
                top: position.bottom != null ? 'auto' : `${position.top}px`,
                bottom:
                  position.bottom != null ? `${position.bottom}px` : 'auto',
                left: `${position.left}px`,
                minWidth: `${position.width}px`
              }}
              id={`${listId}-dropdown-list`}
              role="presentation"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {renderDropdown()}
            </div>,
            document.body
          )
        : isDropdownOpen &&
          !usePortal && (
            <div
              className={`absolute z-20 mt-1 w-55 origin-top-right rounded-md border border-grey40 bg-theme px-2 shadow-sm ${
                resolvedOptionsStyle || 'right-0'
              }`}
              id={`${listId}-dropdown-list`}
            >
              {renderDropdown()}
            </div>
          )}
    </div>
  );
};

export default Dropdown;
